// Emoji Alchemist translation engine.
// Fully local: no API keys, no paid services, no network requests.
//
// How a message is translated, in order:
//   1. Your curated phrases (phrase_mapping.json), longest match first, anywhere in the text.
//   2. Multi-word emoji names from emojibase ("ice cream", "red heart").
//   3. Single words: hand-picked overrides first, then emojibase labels and tags.
//   4. Anything unmatched stays as the original word (like Emojiterra). No placeholders.
//
// Generation accents only append one flair emoji; they never change the matching.
import emojiData from 'emojibase-data/en/compact.json';
import curatedPhrases from '../../phrase_mapping.json';

// ---------------------------------------------------------------------------
// Hand-picked choices where the dictionary pick is weak or ambiguous.
// ---------------------------------------------------------------------------
// Everyday vocabulary, grouped by theme. Hand-picked so common words get the emoji a person
// would choose; emojibase keywords alone pick odd ones (or none) for many of these.
const VOCAB = {
  // Time & frequency
  day: '📅', days: '📅', daily: '📅', every: '🔁', everyday: '🔁', always: '♾️', forever: '♾️',
  never: '🚫', again: '🔁', often: '🔁', sometimes: '🔀', soon: '🔜', now: '⏳', later: '⏳',
  wait: '⏳', minute: '⏱️', minutes: '⏱️', hour: '🕐', hours: '🕐', month: '🗓️', year: '🗓️',
  calendar: '📅', schedule: '🗓️', date: '📅', yesterday: '📅', early: '🌅', past: '⏪',
  future: '🔮', history: '📜', season: '🍂', holiday: '🎉', holidays: '🎉',
  monday: '📅', tuesday: '📅', wednesday: '📅', thursday: '📅', friday: '🎉', saturday: '🎉', sunday: '☀️',
  spring: '🌷', summer: '🏖️', autumn: '🍂', fall: '🍂', winter: '⛄',
  january: '📅', february: '💘', march: '📅', april: '🌧️', june: '☀️', july: '🎆',
  august: '☀️', september: '🍂', october: '🎃', november: '🦃', december: '🎄',
  christmas: '🎄', easter: '🐣', thanksgiving: '🦃', halloween: '🎃', valentine: '💘',
  // Faith & spirit
  bible: '📖', scripture: '📖', verse: '📖', gospel: '📖', church: '⛪', chapel: '⛪',
  pray: '🙏', prayer: '🙏', prayers: '🙏', praying: '🙏', amen: '🙏', bless: '🙏', blessing: '🙏',
  blessed: '😇', grace: '🙏', faith: '🙏', god: '🙏', lord: '🙏', jesus: '✝️', christ: '✝️',
  cross: '✝️', heaven: '😇', angel: '😇', holy: '😇', spirit: '🕊️', soul: '🕊️', peace: '☮️',
  worship: '🙌', praise: '🙌', hallelujah: '🙌', glory: '🙌', choir: '🎼', hymn: '🎼',
  sermon: '🎤', pastor: '🧑‍💼', mosque: '🕌', synagogue: '🕍', temple: '🛕', menorah: '🕎',
  hanukkah: '🕎', ramadan: '🌙', diwali: '🪔', meditate: '🧘', meditation: '🧘', hope: '🤞',
  wish: '🤞', miracle: '✨', forgive: '🤝', mercy: '🙏',
  // People & relationships
  mother: '👩', father: '👨', parent: '👪', parents: '👪', son: '👦', daughter: '👧',
  brother: '👦', sister: '👧', husband: '🤵', wife: '👰', wedding: '💒', marry: '💍',
  married: '💍', ring: '💍', couple: '💑', partner: '🤝', children: '🧒', child: '🧒',
  kids: '🧒', guy: '🙋‍♂️', everyone: '👥', everybody: '👥', someone: '👤', anyone: '👤',
  person: '👤', member: '👤', group: '👥', community: '🏘️', neighbor: '🏡', guest: '🙋',
  customer: '🛍️', client: '🤝', student: '🧑‍🎓', teacher: '🧑‍🏫', nurse: '🧑‍⚕️', police: '👮',
  king: '🤴', queen: '👸', president: '🏛️', baby: '👶', together: '🤝', alone: '🧍', self: '🧍',
  // Feelings & reactions
  feel: '💭', feeling: '💭', fun: '🥳', funny: '😂', joy: '😊', glad: '😊', proud: '🥹',
  grateful: '🙏', thankful: '🙏', thank: '🙏', lonely: '🥺', miss: '🥺', hurt: '🤕', pain: '🤕',
  afraid: '😨', fear: '😨', nervous: '😬', calm: '😌', relax: '😌', rest: '😌', safe: '🛡️',
  hate: '😡', upset: '😤', surprise: '😲', surprised: '😲', shock: '😱', lucky: '🍀', luck: '🍀',
  nice: '😊', awesome: '🤩', amazing: '🤩', beautiful: '😍', cute: '🥰', ugly: '🤢', gross: '🤢',
  perfect: '💯', best: '🏆', better: '📈', worse: '📉', worst: '👎', wrong: '❌', right: '✅',
  correct: '✅', true: '✅', false: '❌', sure: '👍', agree: '👍', fine: '👌', okay: '👌',
  enjoy: '😊', care: '🤗', sweet: '🍬', welcome: '🤗', congrats: '🎉',
  congratulations: '🎉', celebrate: '🎉', cheers: '🥂', remember: '💭', forget: '🤷', know: '🧠',
  understand: '💡', learn: '📚', believe: '🙏', dream: '💭', mind: '🧠', sense: '💭',
  // Everyday actions
  build: '🏗️', fix: '🔧', create: '✨', start: '🏁', begin: '🏁', finish: '🏁',
  end: '🔚', done: '✅', complete: '✅', try: '💪', help: '🆘', give: '🎁', share: '🤝',
  send: '📤', receive: '📥', bring: '🤲', open: '🔓',
  close: '🔒', move: '📦', leave: '🚪', visit: '🚪', stay: '🏠', live: '🏡',
  meet: '🤝', talk: '🗣️', say: '🗣️', tell: '🗣️', speak: '🗣️', ask: '❓', answer: '💬',
  listen: '👂', hear: '👂', sing: '🎤', dance: '💃', play: '🎮', swim: '🏊', cook: '🧑‍🍳',
  clean: '🧹', wash: '🧼', sleepy: '😴', wake: '⏰', smile: '😊', change: '🔄', grow: '🌱',
  win: '🏆', lose: '😞', lost: '😞', pay: '💳', sell: '🏷️', spend: '💸', save: '💰', cost: '💲',
  price: '🏷️', cheap: '🏷️', expensive: '💸', choose: '🤔', decide: '🤔', plan: '📝', study: '📚',
  teach: '🧑‍🏫', test: '📝', show: '👀', find: '🔍', search: '🔍', want: '🤞',
  love: '❤️', follow: '➡️', lead: '🧭', climb: '🧗', ride: '🚴', fight: '🥊',
  die: '🪦', born: '🐣', check: '✅', post: '📮', upload: '📤', download: '📥',
  // Places & things
  place: '📍', around: '🔄', outside: '🌳', inside: '🏠',
  room: '🚪', door: '🚪', kitchen: '🍳', bed: '🛏️', bedroom: '🛏️', bathroom: '🛁', garden: '🌻',
  street: '🛣️', road: '🛣️', town: '🏘️', country: '🗺️', state: '🗺️', map: '🗺️', store: '🏪',
  restaurant: '🍽️', hospital: '🏥', hotel: '🏨', airport: '✈️', bank: '🏦', library: '📚',
  university: '🎓', college: '🎓', class: '🏫', office: '🏢', company: '🏢', business: '💼',
  market: '🛒', mall: '🛍️', farm: '🚜', lake: '🏞️', river: '🏞️', mountain: '⛰️', ocean: '🌊',
  sea: '🌊', island: '🏝️', forest: '🌲', sky: '🌤️', air: '💨', wind: '💨', cloud: '☁️',
  storm: '⛈️', weather: '🌦️', land: '🏞️', nature: '🌿', animal: '🐾', pet: '🐾', bird: '🐦',
  fish: '🐟', horse: '🐴', key: '🔑', box: '📦', bag: '👜', paper: '📄', letter: '✉️',
  card: '💳', ticket: '🎫', news: '📰', story: '📖', word: '🔤', words: '🔤', name: '📛',
  number: '🔢', list: '📝', note: '📝', notes: '📝', page: '📄', site: '🌐', website: '🌐',
  online: '🌐', internet: '🌐', app: '📱', video: '📹', image: '🖼️', photo: '📸',
  light: '🔆', power: '⚡', energy: '⚡', health: '🩺', body: '🧍', face: '🙂', hand: '✋',
  eye: '👁️', eyes: '👀', head: '🗣️', hair: '💇', heart: '❤️', life: '🌱', death: '🪦',
  thing: '📦', things: '📦', stuff: '📦', gift: '🎁', toy: '🧸', clothes: '👕', shirt: '👕',
  hat: '🧢', bike: '🚲', train: '🚆', boat: '⛵', ship: '🚢', truck: '🚚', taxi: '🚕',
  // Work, media & society
  message: '💬', messages: '💬', comment: '💬', chat: '💬', review: '⭐', rating: '⭐', support: '🤝',
  info: 'ℹ️', information: 'ℹ️', report: '📊', data: '📊', research: '🔬', science: '🔬',
  education: '🎓', technology: '💻', tech: '💻', government: '🏛️', law: '⚖️', legal: '⚖️',
  court: '⚖️', tax: '🧾', insurance: '🛡️', security: '🔒', private: '🔒', secret: '🤫',
  sale: '🏷️', deal: '🤝', order: '📦', delivery: '🚚', event: '🎫', concert: '🎤',
  sports: '⚽', sport: '⚽', football: '🏈', soccer: '⚽', basketball: '🏀', baseball: '⚾',
  color: '🎨', colour: '🎨', problem: '⚠️', issue: '⚠️', error: '⚠️', war: '⚔️', popular: '🔥',
  trending: '🔥', natural: '🌿', language: '🗣️', human: '🧑', something: '🤷', nothing: '🫥',
  everything: '🌐', anything: '🤷', club: '🎉', social: '👥', media: '📺', blog: '✍️',
  service: '🛎️', not: '🚫', 'close friend': '🫂', 'best friend': '🫂', 'wait a second': '⏳',
  'just a second': '⏳', 'sunday service': '☀️ 🙌', 'church service': '⛪ 🙌',
  // Qualities & amounts
  more: '➕', less: '➖', most: '🔝', top: '🔝', high: '⬆️', low: '⬇️',   long: '📏', short: '🤏', little: '🤏', tiny: '🤏', huge: '🐘', large: '🐘', full: '🈵',
  empty: '🫙', half: '🌓', whole: '⭕', many: '🔢', few: '🤏', enough: '✋',   old: '👴', young: '🧒', easy: '😌', difficult: '😓', simple: '👌', special: '🌟', important: '❗',
  real: '💯', fake: '🎭', clear: '🔍', quick: '⚡', quiet: '🤫', loud: '📢', rich: '🤑',
  poor: '😢', healthy: '🥗', warm: '☀️', dark: '🌑', bright: '🌟', first: '🥇',
  second: '🥈', third: '🥉', next: '⏭️',   };

const OVERRIDES = {
  ...VOCAB,
  dog: '🐶', puppy: '🐶', pup: '🐶', cat: '🐱', kitten: '🐱', park: '🏞️', pizza: '🍕',
  coffee: '☕', tea: '🍵', love: '❤️', heart: '❤️', happy: '😊', sad: '😢', angry: '😠',
  mad: '😠', tired: '😴', sleep: '😴', work: '💼', job: '💼', office: '🏢', home: '🏠',
  house: '🏠', car: '🚗', drive: '🚗', money: '💰', cash: '💵', food: '🍽️', eat: '🍽️',
  walk: '🚶', run: '🏃', buy: '🛍️', shop: '🛍️', boss: '🧑‍💼', watch: '👀', see: '👀',
  look: '👀', laugh: '😂', lol: '😂', cry: '😭', party: '🎉', music: '🎵', song: '🎶',
  phone: '📱', text: '💬', call: '📞', email: '📧', computer: '💻', laptop: '💻',
  book: '📖', read: '📖', school: '🏫', friend: '🧑‍🤝‍🧑', family: '👨‍👩‍👧', baby: '👶',
  sun: '☀️', sunny: '☀️', rain: '🌧️', snow: '❄️', hot: '🔥', fire: '🔥', cold: '🥶',
  beach: '🏖️', vacation: '🌴', travel: '✈️', plane: '✈️', fly: '✈️', time: '⏰',
  late: '⏰', morning: '🌅', night: '🌙', tonight: '🌙', today: '📅', tomorrow: '📅',
  week: '📅', weekend: '🎉', birthday: '🎂', cake: '🎂', gift: '🎁', win: '🏆',
  idea: '💡', think: '🤔', question: '❓', yes: '✅', no: '🚫', ok: '👌', good: '👍',
  great: '👍', bad: '👎', cool: '😎', wow: '😮', kiss: '😘', hug: '🤗', sick: '🤒',
  doctor: '🧑‍⚕️', gym: '🏋️', exercise: '🏋️', game: '🎮', movie: '🎬', tv: '📺',
  photo: '📸', camera: '📷', picture: '📸', design: '🎨', art: '🎨', paint: '🎨',
  write: '✍️', meeting: '📅', grandma: '👵', grandpa: '👴', mom: '👩', dad: '👨',
  man: '👨', woman: '👩', boy: '👦', girl: '👧', kid: '🧒', people: '👥', team: '👥',
  world: '🌍', earth: '🌍', city: '🏙️', star: '⭐', moon: '🌙', tree: '🌳', flower: '🌸', pretty: '🌸',
  water: '💧', beer: '🍺', wine: '🍷', burger: '🍔', taco: '🌮', sushi: '🍣',
  chicken: '🍗', bread: '🍞', apple: '🍎', cookie: '🍪', chocolate: '🍫', drink: '🥤',
  'ice cream': '🍦', 'hot dog': '🌭', 'thank you': '🙏', thanks: '🙏', please: '🙏',
  "can't believe": '🤯', "can't wait": '🤩', "don't know": '🤷', 'no way': '😱',
  'oh my god': '😱', omg: '😱', 'every day': '🔁 📅', 'every night': '🔁 🌙',
  'god bless': '🙏 😇', 'god bless you': '🙏 😇', 'read the bible': '📖', 'holy spirit': '🕊️',
  'good night': '🌙', 'good morning': '🌅', 'happy birthday': '🎂 🎉', 'merry christmas': '🎄 🎅',
  'happy new year': '🎆 🥂', 'new year': '🎆', 'happy easter': '🐣 ✝️', 'see you': '👋', 'miss you': '🥺 ❤️',
  'love you': '❤️', 'all day': '☀️ 🌙', 'right now': '⏳', 'pretty much': '👌', 'of course': '👍', 'at home': '🏠',
  lmao: '😂', lmfao: '😂', haha: '😂', hahaha: '😂', hehe: '😆', rofl: '🤣',
  yikes: '😬', ugh: '😩', yay: '🎉', cringe: '😬', bruh: '🗿',
  hello: '👋', hi: '👋', hey: '👋', bye: '👋', goodbye: '👋', go: '➡️', stop: '🛑',
  fast: '⚡', slow: '🐢', big: '🐘', small: '🤏', new: '🆕', free: '🆓', hard: '💪',
  strong: '💪', smart: '🧠', brain: '🧠', crazy: '🤪', scared: '😱', excited: '🤩',
  bored: '🥱', confused: '😕', sorry: '😔', dead: '💀', ghost: '👻', wifi: '📶',
  launch: '🚀', worry: '😟', worried: '😟', anxious: '😰', stress: '😫', stressed: '😫',
  news: '📰', glasses: '👓', bus: '🚌', pants: '👖', dress: '👗', shoes: '👟',
};

// Words that carry no picture; dropped from the output.
const FILLER = new Set(`a an the to and or but of in on at for with from by as
  i me my mine you your yours he him his she her hers it its we us our they them their
  is am are was were be been being do does did have has had will would should could
  can just so very really that this these those there here then than too also about
  into onto up down out over some any all
  i'm i've i'll i'd you're you've you'll it's he's she's we're we've they're they've
  that's there's let's
  if what which who whom whose how why when where after before through each because even
  much same while since during another however yet until least further already several
  whether either else though such per via upon one ones get got gets getting gotten make
  makes made making take takes took taken use used uses need needs needed come came comes
  gonna wanna gotta let lets like um uh other others only must shall might still own both
  under within keep put given`.split(/\s+/).filter(Boolean));

// Words that mean "very" when another word follows ("pretty awesome", "quite sure").
// In that position they are dropped like "really"; on their own ("so pretty") they translate.
const SOFT_INTENSIFIERS = new Set(['pretty', 'quite', 'fairly']);

const IRREGULAR = {
  bought: 'buy', ate: 'eat', went: 'go', saw: 'see', ran: 'run', drank: 'drink',
  drove: 'drive', flew: 'fly', made: 'make', got: 'get', took: 'take', wrote: 'write',
  thought: 'think', felt: 'feel', slept: 'sleep', won: 'win', sang: 'sing', stole: 'steal',
  children: 'kid', kids: 'kid', woke: 'wake', going: 'go', goes: 'go', gone: 'go', told: 'tell', said: 'say', heard: 'hear', met: 'meet',
  gave: 'give', sent: 'send', left: 'leave', came: 'come', knew: 'know', grew: 'grow', taught: 'teach', men: 'man', women: 'woman', mice: 'mouse', feet: 'foot',
};

const GENERATION_FLAIR = {
  boomer: '👍', genX: '😎', millennial: '✨', genZ: '💀', genAlpha: '🔥',
};

// ---------------------------------------------------------------------------
// Generation accents: the same meaning, said the way each generation says it.
// `keys` are the emoji the translator produces; each generation column is what that
// generation would send instead (space-separated = several emoji, e.g. Boomer stacking).
// Grounded in how generations report using emoji: 😂 reads "millennial", Gen Z laughs with
// 💀/😭 and reads 👍 and 🙂 as passive-aggressive, Gen Alpha deadpans with 🗿, Boomers
// stack warm emoji and use them literally. Stylized and playful on purpose.
// ---------------------------------------------------------------------------
export const GENERATION_VARIANTS = [
  // Laughs & humor
  {group: 'Laughs & humor', concept: 'Laughing', keys: '😂 🤣 😆 😹', boomer: '😂', genX: '🤣', millennial: '😂', genZ: '💀', genAlpha: '😭'},
  {group: 'Laughs & humor', concept: 'Silly / goofy', keys: '🤪 😜 😝', boomer: '😜', genX: '🤪', millennial: '🤪', genZ: '🤡', genAlpha: '🤪'},
  {group: 'Laughs & humor', concept: 'Sarcasm / irony', keys: '🙃', boomer: '😉', genX: '😏', millennial: '🙃', genZ: '🙂', genAlpha: '🗿'},
  {group: 'Laughs & humor', concept: 'Wink / teasing', keys: '😉', boomer: '😉', genX: '😏', millennial: '😉', genZ: '🤭', genAlpha: '😏'},
  {group: 'Laughs & humor', concept: 'Dead / I can’t', keys: '💀 ☠️', boomer: '😵', genX: '☠️', millennial: '💀', genZ: '💀', genAlpha: '💀'},
  // Love & affection
  {group: 'Love & affection', concept: 'Love', keys: '❤️ ♥️ 💗 💖 💕 💞 💓 💘', boomer: '❤️ ❤️', genX: '❤️', millennial: '💕', genZ: '🫶', genAlpha: '🫶'},
  {group: 'Love & affection', concept: 'Adoring / crush', keys: '😍 🥰', boomer: '😍', genX: '😍', millennial: '🥰', genZ: '🥹', genAlpha: '😍'},
  {group: 'Love & affection', concept: 'Kiss', keys: '😘 💋 😚', boomer: '😘', genX: '💋', millennial: '😘', genZ: '😚', genAlpha: '😘'},
  {group: 'Love & affection', concept: 'Hug', keys: '🤗 🫂', boomer: '🤗', genX: '🤗', millennial: '🫂', genZ: '🫂', genAlpha: '🫂'},
  {group: 'Love & affection', concept: 'Cute / shy', keys: '🥺', boomer: '☺️', genX: '😳', millennial: '🥺', genZ: '👉 👈', genAlpha: '🥺'},
  {group: 'Love & affection', concept: 'Hot / attractive', keys: '🥵', boomer: '😍', genX: '😏', millennial: '🥵', genZ: '🥵', genAlpha: '😳'},
  // Approval & reactions
  {group: 'Approval & reactions', concept: 'OK / got it', keys: '👍 👌 ✅ 🆗', boomer: '👍', genX: '👌', millennial: '👍', genZ: '🫡', genAlpha: '💯'},
  {group: 'Approval & reactions', concept: 'Great / applause', keys: '👏', boomer: '👏', genX: '🤘', millennial: '👏', genZ: '💯', genAlpha: '🔥'},
  {group: 'Approval & reactions', concept: 'Winning / the best', keys: '🏆', boomer: '🏆', genX: '🏆', millennial: '🥇', genZ: '🐐', genAlpha: '🐐'},
  {group: 'Approval & reactions', concept: 'Cool', keys: '😎 🆒', boomer: '😎', genX: '😎', millennial: '😎', genZ: '🥶', genAlpha: '🗿'},
  {group: 'Approval & reactions', concept: 'No / nope', keys: '🚫 ❌ 🙅 🙅‍♀️ 🙅‍♂️', boomer: '🚫', genX: '✋', millennial: '🙅‍♀️', genZ: '🙅', genAlpha: '❌'},
  {group: 'Approval & reactions', concept: 'Lying / cap', keys: '🤥', boomer: '🤥', genX: '🤥', millennial: '🤥', genZ: '🧢', genAlpha: '🧢'},
  // Happy & excited
  {group: 'Happy & excited', concept: 'Happy', keys: '😊 🙂 ☺️ 😀 😃 😄', boomer: '😊 😊', genX: '🙂', millennial: '😁', genZ: '🥹', genAlpha: '😁'},
  {group: 'Happy & excited', concept: 'Excited', keys: '🤩', boomer: '🎉', genX: '🤘', millennial: '🤩', genZ: '🫨', genAlpha: '🫨'},
  {group: 'Happy & excited', concept: 'Party / celebrate', keys: '🎉 🎊 🥳', boomer: '🎈', genX: '🍻', millennial: '🥂', genZ: '🪩', genAlpha: '🕺'},
  {group: 'Happy & excited', concept: 'Birthday', keys: '🎂', boomer: '🎂 🎈', genX: '🎂', millennial: '🎂', genZ: '🎂', genAlpha: '🎂'},
  {group: 'Happy & excited', concept: 'Calm / chill', keys: '😌', boomer: '😌', genX: '😎', millennial: '🧘', genZ: '😌', genAlpha: '🗿'},
  // Sad & stressed
  {group: 'Sad & stressed', concept: 'Crying', keys: '😭', boomer: '😢', genX: '😞', millennial: '😭', genZ: '🥲', genAlpha: '😔'},
  {group: 'Sad & stressed', concept: 'Sad / sorry', keys: '😢 😞 😔 ☹️ 🙁 😿', boomer: '😢', genX: '😞', millennial: '😔', genZ: '🥲', genAlpha: '😔'},
  {group: 'Sad & stressed', concept: 'Overwhelmed', keys: '😩 😫 😵', boomer: '😩', genX: '😫', millennial: '😩', genZ: '🫠', genAlpha: '😵‍💫'},
  {group: 'Sad & stressed', concept: 'Stressed / nervous', keys: '😰 😟 😓 😥 😅', boomer: '😟', genX: '😓', millennial: '😅', genZ: '🙂', genAlpha: '😬'},
  {group: 'Sad & stressed', concept: 'Tired / sleepy', keys: '😴 🥱 😪', boomer: '😴', genX: '🥱', millennial: '😴', genZ: '😪', genAlpha: '💤'},
  {group: 'Sad & stressed', concept: 'Sick', keys: '🤒 🤧 😷', boomer: '🤒', genX: '🤒', millennial: '🤧', genZ: '😷', genAlpha: '🤢'},
  // Anger & attitude
  {group: 'Anger & attitude', concept: 'Angry', keys: '😠 😡 🤬 😤', boomer: '😠', genX: '😤', millennial: '😡', genZ: '😤', genAlpha: '💢'},
  {group: 'Anger & attitude', concept: 'Annoyed / eye roll', keys: '🙄 😒 😑', boomer: '😒', genX: '🙄', millennial: '🙄', genZ: '😑', genAlpha: '🗿'},
  {group: 'Anger & attitude', concept: 'Facepalm / fail', keys: '🤦 🤦‍♀️ 🤦‍♂️', boomer: '🤦', genX: '🙄', millennial: '🤦‍♀️', genZ: '🤡', genAlpha: '🗿'},
  {group: 'Anger & attitude', concept: 'Unbothered / sassy', keys: '💅 💁 💁‍♀️', boomer: '💁', genX: '😏', millennial: '💁‍♀️', genZ: '💅', genAlpha: '💅'},
  // Surprise & awkward
  {group: 'Surprise & awkward', concept: 'Surprised / shocked', keys: '😮 😲 😱 😯 😨', boomer: '😮', genX: '😲', millennial: '😱', genZ: '👁️ 👄 👁️', genAlpha: '🫨'},
  {group: 'Surprise & awkward', concept: 'Mind blown', keys: '🤯', boomer: '😲', genX: '🤯', millennial: '🤯', genZ: '🤯', genAlpha: '🫨'},
  {group: 'Surprise & awkward', concept: 'Embarrassed / awkward', keys: '😳 😬 🙈', boomer: '😳', genX: '😬', millennial: '🙈', genZ: '🫣', genAlpha: '💀'},
  {group: 'Surprise & awkward', concept: 'Thinking / suspicious', keys: '🤔 😕 🤨 🧐', boomer: '🤔', genX: '🤨', millennial: '🤔', genZ: '🤨', genAlpha: '🧐'},
  // Hello & goodbye
  {group: 'Hello & goodbye', concept: 'Hi / bye', keys: '👋', boomer: '👋', genX: '✌️', millennial: '👋', genZ: '👋', genAlpha: '🫡'},
  // Tech & media
  {group: 'Tech & media', concept: 'Phone / call', keys: '📱 📞 ☎️', boomer: '☎️', genX: '📟', millennial: '📱', genZ: '📱', genAlpha: '📱'},
  {group: 'Tech & media', concept: 'Computer', keys: '💻 🖥️', boomer: '🖥️', genX: '💾', millennial: '💻', genZ: '💻', genAlpha: '🎮'},
  {group: 'Tech & media', concept: 'Music', keys: '🎵 🎶 🎧', boomer: '📻', genX: '📼', millennial: '💿', genZ: '🎧', genAlpha: '🎧'},
  {group: 'Tech & media', concept: 'TV / movie', keys: '📺 🎬', boomer: '📺', genX: '📼', millennial: '📺', genZ: '📺', genAlpha: '📱'},
  {group: 'Tech & media', concept: 'Photo', keys: '📸 📷', boomer: '📷', genX: '📷', millennial: '📸', genZ: '🤳', genAlpha: '🤳'},
  {group: 'Tech & media', concept: 'Gaming', keys: '🎮 🕹️', boomer: '🃏', genX: '🕹️', millennial: '🎮', genZ: '🎮', genAlpha: '🎮'},
  // Food & drink
  {group: 'Food & drink', concept: 'Food / eat', keys: '🍽️', boomer: '🍽️', genX: '🍔', millennial: '🥑', genZ: '🍜', genAlpha: '🍕'},
  {group: 'Food & drink', concept: 'Coffee', keys: '☕', boomer: '☕', genX: '☕', millennial: '☕', genZ: '🧋', genAlpha: '🥤'},
  {group: 'Food & drink', concept: 'Drinks / cheers', keys: '🍺 🍷 🍻 🥂 🍸', boomer: '🍷', genX: '🍺', millennial: '🥂', genZ: '🍹', genAlpha: '🧃'},
  // Work & money
  {group: 'Work & money', concept: 'Work / office', keys: '💼 🏢', boomer: '💼', genX: '📠', millennial: '💻', genZ: '🧑‍💻', genAlpha: '📱'},
  {group: 'Work & money', concept: 'Money', keys: '💰 💵 💸 🤑', boomer: '💵', genX: '💰', millennial: '💸', genZ: '💸', genAlpha: '🤑'},
  {group: 'Work & money', concept: 'Smart / idea', keys: '💡', boomer: '💡', genX: '💡', millennial: '💡', genZ: '🧠', genAlpha: '🧠'},
  // People & style
  {group: 'People & style', concept: 'Friends / squad', keys: '🧑‍🤝‍🧑 👥 👯 👯‍♀️', boomer: '👫', genX: '👥', millennial: '👯‍♀️', genZ: '🫂', genAlpha: '🫂'},
  {group: 'People & style', concept: 'Pretty / beauty', keys: '🌸 🌹 💄', boomer: '🌹', genX: '💄', millennial: '🌸', genZ: '💅', genAlpha: '✨'},
];

let variantIndex = null;
const variantFor = (emoji, generation) => {
  if (!variantIndex) {
    variantIndex = new Map();
    for (const row of GENERATION_VARIANTS) {
      for (const key of row.keys.split(' ')) variantIndex.set(key.replace(/️/g, ''), row);
    }
  }
  const row = variantIndex.get(String(emoji).replace(/️/g, ''));
  const value = row && has(row, generation) ? row[generation] : null;
  return value ? value.split(' ') : [emoji];
};

// ---------------------------------------------------------------------------
// Text helpers. Input and phrase keys go through the same steps so they match.
// ---------------------------------------------------------------------------
const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

// Keeps case so unknown words can be passed through exactly as typed.
const prep = (value) => String(value ?? '')
  .replace(/[‘’‛`´]/g, "'")
  .replace(/[“”]/g, '"')
  .replace(/(\p{L})[-‐‑–—](?=\p{L})/gu, '$1 ') // "ice-cream" -> "ice cream"
  .replace(/\s+/g, ' ')
  .trim();

// Lowercase and trim punctuation at the edges; inner apostrophes survive ("don't").
const clean = (token) => token.toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '');

const keyOf = (value) => prep(value).split(' ').map(clean).filter(Boolean).join(' ');

const PICTOGRAPH = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u;

export const isEmojiToken = (value) => PICTOGRAPH.test(String(value ?? ''));

// ---------------------------------------------------------------------------
// Dictionary built once, at load time, from the bundled emojibase data.
// ---------------------------------------------------------------------------
const stripVariation = (value) => String(value ?? '').replace(/\uFE0F/g, '');

function buildDictionary(entries) {
  const words = new Map(); // word -> {emoji, score}; lower score wins
  const phrases = new Map(); // "ice cream" -> emoji
  const names = new Map(); // emoji (without variation selector) -> official name
  const put = (word, emoji, score) => {
    if (!word || word.length < 2 || !/^[a-z0-9']+$/.test(word)) return;
    const current = words.get(word);
    if (!current || score < current.score) words.set(word, {emoji, score});
  };

  for (const entry of entries) {
    // Skip skin-tone/hair components and lone regional-indicator letters.
    if (!entry || !entry.unicode || entry.group === undefined || entry.group === 2) continue;
    if (entry.label) names.set(stripVariation(entry.unicode), String(entry.label));
    const order = (entry.order || 0) / 1e6; // tie-break toward more common emoji
    const label = keyOf(String(entry.label || '').replace(/^flag: /i, ''));
    const parts = label ? label.split(' ') : [];

    if (parts.length === 1) put(parts[0], entry.unicode, 0 + order);
    if (parts.length === 2 && parts[1] === 'face') put(parts[0], entry.unicode, 1 + order);
    if (parts.length > 1) {
      put(parts[parts.length - 1], entry.unicode, 2 + order); // head noun: "national park"
      if (parts.length <= 3 && !phrases.has(label)) phrases.set(label, entry.unicode);
      const tail = parts.slice(-2).join(' '); // "soft ice cream" -> "ice cream"
      if (parts.length === 3 && !phrases.has(tail)) phrases.set(tail, entry.unicode);
    }
    (entry.tags || []).forEach((tag, index) => {
      const word = clean(String(tag));
      // Flags are tagged with country codes ("CA", "DO", "IS"), which collide with words.
      if (entry.group === 9 && word.length <= 3) return;
      put(word, entry.unicode, 3 + index * 0.01 + order);
    });
  }

  const wordMap = new Map();
  for (const [word, {emoji}] of words) wordMap.set(word, emoji);
  return {words: wordMap, phrases, names};
}

const DICTIONARY = buildDictionary(Array.isArray(emojiData) ? emojiData : []);

// One phrase table. Later sets win: dictionary < overrides < your curated phrases.
const PHRASES = new Map();
const addPhrase = (phrase, value) => {
  const key = keyOf(phrase);
  const emojis = (Array.isArray(value) ? value : [value])
    .flatMap((emoji) => String(emoji ?? '').split(/\s+/)) // '🔁 📅' is two emoji
    .map((emoji) => emoji.replace(/^#/, '').trim())
    .filter(isEmojiToken);
  if (key && emojis.length) PHRASES.set(key, emojis);
};
for (const [phrase, emoji] of DICTIONARY.phrases) addPhrase(phrase, emoji);
for (const [phrase, emoji] of Object.entries(OVERRIDES)) if (phrase.includes(' ')) addPhrase(phrase, emoji);
if (curatedPhrases && typeof curatedPhrases === 'object') {
  for (const [phrase, emojis] of Object.entries(curatedPhrases)) addPhrase(phrase, emojis);
}

const MAX_PHRASE_WORDS = Math.max(2, ...[...PHRASES.keys()].map((key) => key.split(' ').length));

// Candidate base forms for simple English inflections (exact word first).
function baseForms(word) {
  const forms = [word];
  // Derived forms must keep at least 3 letters, so a stem never collapses into a
  // two-letter code ("can't" -> "ca" matched the Canada flag).
  const add = (form) => form && form.length > 2 && !forms.includes(form) && forms.push(form);
  if (has(IRREGULAR, word) && !forms.includes(IRREGULAR[word])) forms.push(IRREGULAR[word]);
  if (word.includes("n't")) return forms; // contractions stay whole: "can't", "don't"
  if (word.endsWith("'s")) add(word.slice(0, -2));
  if (word.endsWith('ies')) add(`${word.slice(0, -3)}y`);
  if (word.endsWith('es')) add(word.slice(0, -2));
  if (word.endsWith('s') && !word.endsWith('ss')) add(word.slice(0, -1));
  if (word.endsWith('ied')) add(`${word.slice(0, -3)}y`);
  if (word.endsWith('ed')) { add(word.slice(0, -2)); add(word.slice(0, -1)); }
  if (word.endsWith('ing')) { add(word.slice(0, -3)); add(`${word.slice(0, -3)}e`); }
  const doubled = word.match(/^(.*?)([bdgklmnprt])\2(ed|ing)$/); // "running" -> "run"
  if (doubled) add(doubled[1] + doubled[2]);
  return forms;
}

function lookupWord(word) {
  // Curated exact > curated base form > dictionary exact > dictionary base form.
  if (has(OVERRIDES, word)) return OVERRIDES[word];
  const forms = baseForms(word);
  for (const form of forms) if (has(OVERRIDES, form)) return OVERRIDES[form];
  for (const form of forms) if (DICTIONARY.words.has(form)) return DICTIONARY.words.get(form);
  return null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Translate text into an emoji sequence.
 * @returns {{emojis: string[], emojiChain: string, tokens: Array<{text: string, emojis: string[]|null}>}}
 * `emojis` mixes emoji and pass-through words, in reading order. Render as text, never HTML.
 */
export function translate(input) {
  const originals = prep(input).split(' ').filter(Boolean);
  const words = originals.map(clean); // same length and order as `originals`
  const tokens = [];

  for (let i = 0; i < words.length;) {
    const word = words[i];

    // No letters or digits: keep emoji the user typed, drop bare punctuation.
    if (!word) {
      if (isEmojiToken(originals[i])) tokens.push({text: originals[i], emojis: [originals[i]], typed: true});
      i += 1;
      continue;
    }

    // Longest phrase that starts here.
    let size = Math.min(MAX_PHRASE_WORDS, words.length - i);
    for (; size >= 2; size -= 1) {
      const slice = words.slice(i, i + size);
      if (slice.some((part) => !part)) continue;
      const hit = PHRASES.get(slice.join(' '));
      if (hit) {
        tokens.push({text: originals.slice(i, i + size).join(' '), emojis: hit});
        break;
      }
    }
    if (size >= 2) { i += size; continue; }

    // "pretty awesome" = "very awesome": skip the intensifier when a word follows in the same
    // sentence. Punctuation after it ("so pretty. Then...") keeps its own meaning.
    const nextWord = words[i + 1];
    const endsClause = /[.,!?;:]["')\]]*$/.test(originals[i]);
    if (SOFT_INTENSIFIERS.has(word) && nextWord && !FILLER.has(nextWord) && !endsClause) { i += 1; continue; }

    // Single word: hand-picked override, then a one-word phrase entry ("based"), then dictionary.
    if (has(OVERRIDES, word)) {
      tokens.push({text: originals[i], emojis: [OVERRIDES[word]]});
    } else if (PHRASES.has(word)) {
      tokens.push({text: originals[i], emojis: PHRASES.get(word)});
    } else if (!FILLER.has(word)) {
      const emoji = lookupWord(word);
      tokens.push({text: originals[i], emojis: emoji ? [emoji] : null});
    }
    i += 1;
  }

  const emojis = tokens.flatMap((token) => {
    if (token.emojis) return token.emojis;
    const word = token.text.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
    return word ? [word] : [];
  });
  return {emojis, emojiChain: emojis.join(' '), tokens};
}

/**
 * Drop-in replacement for the old pipeline entry point used by EmojiTranslator.jsx.
 * Extra arguments (tone, pipeline) are accepted for compatibility and ignored.
 */
export function translateToEmoji(message, generation = 'millennial') {
  const result = translate(message);
  // Re-voice each translated emoji for the chosen generation. Emoji the user typed are theirs
  // and stay as typed; words with no emoji stay as text.
  const voiced = result.tokens.flatMap((token) => {
    if (!token.emojis) {
      const word = token.text.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
      return word ? [word] : [];
    }
    if (token.typed) return token.emojis;
    return token.emojis.flatMap((emoji) => variantFor(emoji, generation));
  });
  const flair = has(GENERATION_FLAIR, generation) ? GENERATION_FLAIR[generation] : null;
  const emojis = voiced.length && flair ? [...voiced, flair] : voiced;
  return {
    ...result,
    emojis,
    emojiChain: emojis.join(' '),
    tone: generation,
    input: String(message ?? ''),
    source: 'local-engine',
  };
}

/** Official emoji name ("dog face"), or null when unknown. */
export function emojiName(emoji) {
  return DICTIONARY.names.get(stripVariation(emoji)) || null;
}

export {GENERATION_FLAIR};
export default translateToEmoji;